/**
 * CAOS-601 — which part of a long list is worth having in the DOM, and how much empty space to
 * leave above and below it so the scrollbar still tells the truth.
 *
 * The incumbent ships a separate component for a list that renders this way, which means a page
 * builder has to know in advance that a list will get long and pick a different piece when it does
 * — and has to pick again, by hand, on every surface where the same object turns out to have more
 * records than expected. Virtualisation is a RENDERING STRATEGY, not a kind of list. So it lives
 * here, as arithmetic two components apply to themselves, and a page author never chooses it.
 *
 * This module is deliberately pure: no DOM, no measuring, no scroll listener. It takes the numbers a
 * component has already measured and returns the window. That is what makes it testable at every
 * boundary — the first row, the last row, a viewport taller than the content, a row height nobody
 * has measured yet — instead of only through a rendered table.
 */

/** What a component knows about its own list at the moment it is about to paint. */
export interface VirtualWindowInput {
  /** How many items there are in total. */
  readonly count: number;
  /** How tall one ROW of items is, in pixels. Zero or less means "not measured yet". */
  readonly rowHeight: number;
  /** How much of the scroll container is on screen, in pixels. */
  readonly viewport: number;
  /** How far down it is scrolled, in pixels. */
  readonly scrollTop: number;
  /** Items per row — 1 for a table, the column count for a grid. */
  readonly columns?: number;
  /** Extra rows kept either side of the visible band, so a scroll does not paint bare. */
  readonly overscan?: number;
}

/** The slice to render, and the empty space standing in for what is not rendered. */
export interface VirtualWindow {
  /** First item index to render, inclusive. */
  readonly start: number;
  /** Last item index to render, exclusive. */
  readonly end: number;
  /** Pixels of empty space to leave above the rendered slice. */
  readonly leading: number;
  /** Pixels of empty space to leave below it. */
  readonly trailing: number;
  /**
   * Whether anything is actually being held back. False means the whole list is in the window, and
   * a component can skip its spacers entirely rather than writing two zero-height ones.
   */
  readonly windowed: boolean;
}

/**
 * Below this many items, everything is rendered and nothing is measured or listened to.
 *
 * A window has a cost — a scroll listener, a measurement, two spacers, and a re-render whenever the
 * band moves — and a list of thirty rows is not paying it back. Making short lists behave EXACTLY as
 * they did before is also what lets this be turned on for everybody at once: the case that was
 * already fine is not touched, so there is nothing for it to regress.
 */
export const VIRTUAL_THRESHOLD = 120;

/** Rows kept either side of the visible band. Roughly half a screen at typical row heights. */
export const DEFAULT_OVERSCAN = 6;

/** The whole list, as a window — what a component renders when there is nothing to hold back. */
export function wholeList(count: number): VirtualWindow {
  return { start: 0, end: Math.max(0, count), leading: 0, trailing: 0, windowed: false };
}

/**
 * The slice worth rendering for this scroll position.
 *
 * Returns the WHOLE list — not an empty window — whenever it cannot do better than that: too few
 * items to be worth it, a row height nobody has measured, or a viewport of zero (a container that
 * is not on screen yet, which is exactly when a first paint happens). Failing towards "render
 * everything" is the direction that can only cost time; failing the other way renders nothing and
 * looks like data loss.
 */
export function virtualWindow(input: VirtualWindowInput): VirtualWindow {
  const count = Math.max(0, Math.floor(input.count));
  const columns = Math.max(1, Math.floor(input.columns ?? 1));
  const overscan = Math.max(0, Math.floor(input.overscan ?? DEFAULT_OVERSCAN));

  if (count === 0) return wholeList(0);
  if (count < VIRTUAL_THRESHOLD) return wholeList(count);
  if (!(input.rowHeight > 0)) return wholeList(count);
  if (!(input.viewport > 0)) return wholeList(count);

  const rows = Math.ceil(count / columns);
  const scrollTop = Math.max(0, input.scrollTop);

  const firstVisible = Math.floor(scrollTop / input.rowHeight);
  const lastVisible = Math.ceil((scrollTop + input.viewport) / input.rowHeight);

  // A scroll position outlives the list it was taken against: filter a long table down, sort it,
  // collapse a parent row, and the container's own scrollTop does not move until the browser gets
  // round to clamping it. Unclamped, that puts the whole band past the end of the shorter list —
  // a start beyond the end, nothing rendered at all, and the entire height held as empty space
  // above it. That is precisely the "renders nothing and looks like data loss" failure the top of
  // this file says it will not have, so the first row is held inside the list it is indexing.
  const firstRow = Math.min(Math.max(0, firstVisible - overscan), Math.max(0, rows - 1));
  const lastRow = Math.min(rows, Math.max(firstRow + 1, lastVisible + overscan));

  const start = firstRow * columns;
  const end = Math.min(count, lastRow * columns);

  // If the band covers everything anyway, say so rather than drawing two zero-height spacers.
  if (start === 0 && end === count) return wholeList(count);

  return {
    start,
    end,
    leading: firstRow * input.rowHeight,
    trailing: (rows - lastRow) * input.rowHeight,
    windowed: true,
  };
}

/**
 * A window that is guaranteed to contain `index`, for a component moving focus to an item that may
 * not be rendered. Keyboard navigation is the reason this exists: arrowing down the last rendered
 * row must step onto the next record, not stop at the edge of a rendering optimisation the reader
 * cannot see and did not ask for.
 */
export function windowContaining(current: VirtualWindow, index: number, input: VirtualWindowInput): VirtualWindow {
  if (!current.windowed) return current;
  if (index >= current.start && index < current.end) return current;

  const count = Math.max(0, Math.floor(input.count));
  const columns = Math.max(1, Math.floor(input.columns ?? 1));
  const rows = Math.ceil(count / columns);
  const span = Math.max(1, Math.ceil((current.end - current.start) / columns));
  const target = Math.floor(Math.max(0, Math.min(count - 1, index)) / columns);

  const firstRow = Math.max(0, Math.min(rows - span, target - Math.floor(span / 2)));
  const lastRow = Math.min(rows, firstRow + span);

  return {
    start: firstRow * columns,
    end: Math.min(count, lastRow * columns),
    leading: firstRow * input.rowHeight,
    trailing: (rows - lastRow) * input.rowHeight,
    windowed: true,
  };
}
